import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bot, SendHorizonal, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAssistant } from "../contexts/AssistantContext";
import { AssistantMessageContent } from "./AssistantMessageContent";

const TypingIndicator: React.FC = () => (
	<div className="flex items-center gap-1 py-1" aria-hidden="true">
		{[0, 150, 300].map(delay => (
			<span
				key={delay}
				className="h-2 w-2 rounded-full bg-muted-foreground/60 animate-bounce"
				style={{ animationDelay: `${delay}ms` }}
			/>
		))}
	</div>
);

export const AssistantPanel: React.FC = () => {
	const { t } = useTranslation();
	const {
		close,
		messages,
		draft,
		setDraft,
		sendMessage,
		isSending,
		errorKey,
		clearConversation,
		suggestions,
		isLoadingSuggestions,
	} = useAssistant();

	const scrollRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
	}, [messages, isSending, errorKey]);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	useEffect(() => {
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === "Escape") close();
		};
		window.addEventListener("keydown", closeOnEscape);
		return () => window.removeEventListener("keydown", closeOnEscape);
	}, [close]);

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		sendMessage(draft);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			sendMessage(draft);
		}
	};

	const isEmpty = messages.length === 0;
	// Backend errors arrive as translation keys; fall back when a key has no dashboard translation.
	const errorMessage = errorKey && (t(errorKey) === errorKey ? t("assistant.errors.generic") : t(errorKey));

	return (
		<div
			role="dialog"
			aria-modal="false"
			aria-label={t("assistant.title")}
			// Drops below the 5rem header, anchored to the launcher; `end-*` mirrors automatically in RTL.
			className="fixed bottom-1 end-4 sm:end-6 z-50 flex flex-col w-[calc(100vw-2rem)] sm:w-[500px] max-w-full h-[600px] max-h-[90vh] rounded-2xl border border-border bg-card shadow-2xl overflow-hidden"
		>
			<header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-primary">
				<div className="flex items-center gap-2 text-white">
					<Sparkles className="h-5 w-5" />
					<div className="leading-tight">
						<p className="text-sm font-semibold">{t("assistant.title")}</p>
						<p className="text-[11px] opacity-80">{t("assistant.subtitle")}</p>
					</div>
				</div>
				<div className="flex items-center gap-1">
					{!isEmpty && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-8 mx-2 bg-black/10 p-0 px-2 text-white hover:bg-white/20"
							onClick={clearConversation}
							title={t("assistant.newConversation")}
							aria-label={t("assistant.newConversation")}
						>
							{t("assistant.newChat")}
						</Button>
					)}
					<Button
						type="button"
						variant="ghost"
						size="sm"
						className="h-8 w-8 p-0 bg-black/10 text-white hover:bg-white/20"
						onClick={close}
						title={t("common.close")}
						aria-label={t("common.close")}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</header>

			<div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" aria-live="polite">
				{isEmpty && (
					<div className="text-center py-6 space-y-2">
						<Bot className="h-8 w-8 mx-auto text-muted-foreground" />
						<p className="text-sm font-medium text-foreground">{t("assistant.emptyTitle")}</p>
						<p className="text-xs text-muted-foreground">{t("assistant.emptyDescription")}</p>
					</div>
				)}

				{messages.map((message, index) =>
					message.role === "user" ? (
						<div key={index} className="flex justify-end">
							<div className="max-w-[85%] rounded-2xl rounded-ee-sm bg-secondary px-3 py-2 text-sm text-foreground whitespace-pre-wrap break-words">
								{message.content}
							</div>
						</div>
					) : (
						<div key={index} className="flex justify-start">
							<div className="max-w-[90%] rounded-2xl rounded-es-sm bg-muted px-3 py-2 text-foreground">
								<AssistantMessageContent text={message.content} />
							</div>
						</div>
					),
				)}

				{isSending && (
					<div className="flex justify-start">
						<div className="rounded-2xl rounded-es-sm bg-muted px-3 py-2">
							<TypingIndicator />
							<span className="sr-only">{t("assistant.thinking")}</span>
						</div>
					</div>
				)}

				{errorMessage && (
					<div
						role="alert"
						className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive"
					>
						{errorMessage}
					</div>
				)}
			</div>

			{isEmpty && !isLoadingSuggestions && suggestions.length > 0 && (
				<div className="px-4 pb-2 flex flex-wrap gap-2">
					{suggestions.map(suggestion => (
						<button
							key={suggestion.id}
							type="button"
							onClick={() => sendMessage(suggestion.label)}
							disabled={isSending}
							className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
						>
							{suggestion.label}
						</button>
					))}
				</div>
			)}

			<form onSubmit={handleSubmit} className="flex items-end gap-2 border-t border-border p-3">
				<textarea
					ref={inputRef}
					value={draft}
					onChange={event => setDraft(event.target.value)}
					onKeyDown={handleKeyDown}
					rows={1}
					maxLength={4000}
					placeholder={t("assistant.inputPlaceholder")}
					aria-label={t("assistant.inputPlaceholder")}
					className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm max-h-24 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
				/>
				<Button
					type="submit"
					size="sm"
					className="h-10 w-20 p-0 shrink-0"
					disabled={isSending || draft.trim() === ""}
					title={t("assistant.send")}
					aria-label={t("assistant.send")}
				>
					<SendHorizonal className="h-5 w-5 rtl:rotate-180" />
				</Button>
			</form>
		</div>
	);
};
